import React, { useState, useRef } from 'react';
import 'katex/dist/katex.min.css';
import katex from 'katex';

const TOOLBAR = {
  'Base': [
    { label: 'a/b', title: 'Fraction', insert: '\\frac{•}{•}' },
    { label: 'x²', title: 'Exposant', insert: '^{•}' },
    { label: 'xₙ', title: 'Indice', insert: '_{•}' },
    { label: 'x²ₙ', title: 'Exposant + Indice', insert: '^{•}_{•}' },
    { label: '√x', title: 'Racine carrée', insert: '\\sqrt{•}' },
    { label: '∛x', title: 'Racine cubique', insert: '\\sqrt[3]{•}' },
    { label: 'ⁿ√x', title: 'Racine n-ième', insert: '\\sqrt[•]{•}' },
    { label: '|x|', title: 'Valeur absolue', insert: '\\left|•\\right|' },
    { label: '‖x‖', title: 'Norme', insert: '\\left\\|•\\right\\|' },
    { label: '⌊x⌋', title: 'Partie entière inf', insert: '\\lfloor •\\rfloor' },
    { label: '⌈x⌉', title: 'Partie entière sup', insert: '\\lceil •\\rceil' },
    { label: '±', title: 'Plus ou moins', insert: '\\pm' },
    { label: '∓', title: 'Moins ou plus', insert: '\\mp' },
    { label: '×', title: 'Multiplication', insert: '\\times' },
    { label: '÷', title: 'Division', insert: '\\div' },
    { label: '·', title: 'Point milieu', insert: '\\cdot' },
    { label: '∘', title: 'Composition', insert: '\\circ' },
  ],
  'Calcul': [
    { label: '∫', title: 'Intégrale simple', insert: '\\int_{•}^{•}' },
    { label: '∬', title: 'Intégrale double', insert: '\\iint_{•}' },
    { label: '∭', title: 'Intégrale triple', insert: '\\iiint_{•}' },
    { label: '∮', title: 'Intégrale curviligne', insert: '\\oint_{•}' },
    { label: 'Σ', title: 'Somme avec bornes', insert: '\\sum_{•=•}^{•}' },
    { label: 'Π', title: 'Produit avec bornes', insert: '\\prod_{•=•}^{•}' },
    { label: 'lim', title: 'Limite', insert: '\\lim_{•\\to•}' },
    { label: 'lim→∞', title: 'Limite à l\'infini', insert: '\\lim_{•\\to\\infty}' },
    { label: 'd/dx', title: 'Dérivée', insert: '\\frac{d•}{d•}' },
    { label: 'd²/dx²', title: 'Dérivée seconde', insert: '\\frac{d^2•}{d•^2}' },
    { label: 'dⁿ/dxⁿ', title: 'Dérivée n-ième', insert: '\\frac{d^{•}•}{d•^{•}}' },
    { label: '∂/∂x', title: 'Dérivée partielle', insert: '\\frac{\\partial •}{\\partial •}' },
    { label: '∂²', title: 'Dérivée partielle 2', insert: '\\frac{\\partial^2 •}{\\partial •^2}' },
    { label: "f'", title: 'Dérivée prime', insert: "{•}'" },
    { label: "f''", title: 'Dérivée seconde prime', insert: "{•}''" },
    { label: 'ẋ', title: 'Dérivée (point)', insert: '\\dot{•}' },
    { label: 'ẍ', title: 'Dérivée 2nde (point)', insert: '\\ddot{•}' },
    { label: '∞', title: 'Infini', insert: '\\infty' },
  ],
  'Vecteurs': [
    { label: 'v⃗', title: 'Vecteur', insert: '\\vec{•}' },
    { label: 'v̂', title: 'Vecteur unitaire', insert: '\\hat{•}' },
    { label: 'v̄', title: 'Barre dessus', insert: '\\overline{•}' },
    { label: '→', title: 'Flèche dessus', insert: '\\overrightarrow{•}' },
    { label: '∇', title: 'Nabla', insert: '\\nabla' },
    { label: 'grad', title: 'Gradient', insert: '\\nabla •' },
    { label: 'div', title: 'Divergence', insert: '\\nabla \\cdot •' },
    { label: 'rot', title: 'Rotationnel', insert: '\\nabla \\times •' },
    { label: '∇²', title: 'Laplacien', insert: '\\nabla^2 •' },
    { label: 'a·b', title: 'Produit scalaire', insert: '\\vec{•}\\cdot\\vec{•}' },
    { label: 'a×b', title: 'Produit vectoriel', insert: '\\vec{•}\\times\\vec{•}' },
    { label: 'M2×2', title: 'Matrice 2×2', insert: '\\begin{pmatrix}•&•\\\\•&•\\end{pmatrix}' },
    { label: 'M3×3', title: 'Matrice 3×3', insert: '\\begin{pmatrix}•&•&•\\\\•&•&•\\\\•&•&•\\end{pmatrix}' },
    { label: 'col', title: 'Vecteur colonne', insert: '\\begin{pmatrix}•\\\\•\\\\•\\end{pmatrix}' },
    { label: 'det', title: 'Déterminant', insert: '\\det\\begin{pmatrix}•&•\\\\•&•\\end{pmatrix}' },
    { label: 'Aᵀ', title: 'Transposée', insert: '{•}^T' },
    { label: 'A⁻¹', title: 'Inverse', insert: '{•}^{-1}' },
  ],
  'Ensembles': [
    { label: 'ℕ', title: 'Entiers naturels', insert: '\\mathbb{N}' },
    { label: 'ℤ', title: 'Entiers relatifs', insert: '\\mathbb{Z}' },
    { label: 'ℚ', title: 'Rationnels', insert: '\\mathbb{Q}' },
    { label: 'ℝ', title: 'Réels', insert: '\\mathbb{R}' },
    { label: 'ℂ', title: 'Complexes', insert: '\\mathbb{C}' },
    { label: 'ℝⁿ', title: 'Espace réel n-dim', insert: '\\mathbb{R}^{•}' },
    { label: '∅', title: 'Ensemble vide', insert: '\\emptyset' },
    { label: '∈', title: 'Appartient', insert: '\\in' },
    { label: '∉', title: "N'appartient pas", insert: '\\notin' },
    { label: '⊂', title: 'Inclus strictement', insert: '\\subset' },
    { label: '⊆', title: 'Inclus ou égal', insert: '\\subseteq' },
    { label: '∪', title: 'Union', insert: '\\cup' },
    { label: '∩', title: 'Intersection', insert: '\\cap' },
    { label: '\\', title: 'Différence', insert: '\\setminus' },
    { label: 'P(E)', title: 'Parties de E', insert: '\\mathcal{P}(•)' },
    { label: 'Cₙᵏ', title: 'Combinaison', insert: '\\binom{•}{•}' },
    { label: 'Aₙᵏ', title: 'Arrangement', insert: 'A_{•}^{•}' },
    { label: 'n!', title: 'Factorielle', insert: '{•}!' },
    { label: '[a,b]', title: 'Intervalle fermé', insert: '[•,•]' },
    { label: ']a,b[', title: 'Intervalle ouvert', insert: ']•,•[' },
  ],
  'Hilbert': [
    { label: '⟨ψ|', title: 'Bra', insert: '\\langle •|' },
    { label: '|ψ⟩', title: 'Ket', insert: '|•\\rangle' },
    { label: '⟨ψ|φ⟩', title: 'Braket', insert: '\\langle •|•\\rangle' },
    { label: '|ψ⟩⟨ψ|', title: 'Projecteur', insert: '|•\\rangle\\langle •|' },
    { label: '⟨Â⟩', title: 'Valeur moyenne', insert: '\\langle\\hat{•}\\rangle' },
    { label: 'Â', title: 'Opérateur', insert: '\\hat{•}' },
    { label: 'Ĥ', title: 'Hamiltonien', insert: '\\hat{H}' },
    { label: 'ℋ', title: 'Espace de Hilbert', insert: '\\mathcal{H}' },
    { label: 'L²', title: 'Espace L²', insert: 'L^2(•)' },
    { label: '⟨f,g⟩', title: 'Produit scalaire', insert: '\\langle •,•\\rangle' },
    { label: 'Tr', title: 'Trace', insert: '\\text{Tr}(•)' },
    { label: 'A†', title: 'Adjoint', insert: '{•}^\\dagger' },
    { label: '[A,B]', title: 'Commutateur', insert: '[•,•]' },
    { label: '{A,B}', title: 'Anti-commutateur', insert: '\\{•,•\\}' },
    { label: 'ℏ', title: 'h-barre', insert: '\\hbar' },
    { label: 'ψ*', title: 'Conjugué complexe', insert: '{•}^*' },
    { label: 'σ(A)', title: 'Spectre', insert: '\\sigma(•)' },
  ],
  'Grec': [
    { label: 'α', title: 'Alpha', insert: '\\alpha' },
    { label: 'β', title: 'Beta', insert: '\\beta' },
    { label: 'γ', title: 'Gamma', insert: '\\gamma' },
    { label: 'δ', title: 'Delta', insert: '\\delta' },
    { label: 'ε', title: 'Epsilon', insert: '\\epsilon' },
    { label: 'ζ', title: 'Zeta', insert: '\\zeta' },
    { label: 'η', title: 'Eta', insert: '\\eta' },
    { label: 'θ', title: 'Theta', insert: '\\theta' },
    { label: 'λ', title: 'Lambda', insert: '\\lambda' },
    { label: 'μ', title: 'Mu', insert: '\\mu' },
    { label: 'ν', title: 'Nu', insert: '\\nu' },
    { label: 'ξ', title: 'Xi', insert: '\\xi' },
    { label: 'π', title: 'Pi', insert: '\\pi' },
    { label: 'ρ', title: 'Rho', insert: '\\rho' },
    { label: 'σ', title: 'Sigma', insert: '\\sigma' },
    { label: 'τ', title: 'Tau', insert: '\\tau' },
    { label: 'φ', title: 'Phi', insert: '\\phi' },
    { label: 'χ', title: 'Chi', insert: '\\chi' },
    { label: 'ψ', title: 'Psi', insert: '\\psi' },
    { label: 'ω', title: 'Omega', insert: '\\omega' },
    { label: 'Γ', title: 'Gamma maj', insert: '\\Gamma' },
    { label: 'Δ', title: 'Delta maj', insert: '\\Delta' },
    { label: 'Θ', title: 'Theta maj', insert: '\\Theta' },
    { label: 'Λ', title: 'Lambda maj', insert: '\\Lambda' },
    { label: 'Π', title: 'Pi maj', insert: '\\Pi' },
    { label: 'Σ', title: 'Sigma maj', insert: '\\Sigma' },
    { label: 'Φ', title: 'Phi maj', insert: '\\Phi' },
    { label: 'Ψ', title: 'Psi maj', insert: '\\Psi' },
    { label: 'Ω', title: 'Omega maj', insert: '\\Omega' },
  ],
  'Logique': [
    { label: '∀', title: 'Pour tout', insert: '\\forall' },
    { label: '∃', title: 'Il existe', insert: '\\exists' },
    { label: '∄', title: "Il n'existe pas", insert: '\\nexists' },
    { label: '¬', title: 'Négation', insert: '\\neg' },
    { label: '∧', title: 'Et logique', insert: '\\land' },
    { label: '∨', title: 'Ou logique', insert: '\\lor' },
    { label: '⟹', title: 'Implique', insert: '\\Rightarrow' },
    { label: '⟺', title: 'Équivalence', insert: '\\Leftrightarrow' },
    { label: '≡', title: 'Équivalent', insert: '\\equiv' },
    { label: '≠', title: 'Différent', insert: '\\neq' },
    { label: '≤', title: 'Inférieur ou égal', insert: '\\leq' },
    { label: '≥', title: 'Supérieur ou égal', insert: '\\geq' },
    { label: '≪', title: 'Très inférieur', insert: '\\ll' },
    { label: '≫', title: 'Très supérieur', insert: '\\gg' },
    { label: '≈', title: 'Approximativement', insert: '\\approx' },
    { label: '∼', title: 'Équivalent asymp.', insert: '\\sim' },
    { label: '∝', title: 'Proportionnel', insert: '\\propto' },
  ],
  'Fonctions': [
    { label: 'sin', title: 'Sinus', insert: '\\sin(•)' },
    { label: 'cos', title: 'Cosinus', insert: '\\cos(•)' },
    { label: 'tan', title: 'Tangente', insert: '\\tan(•)' },
    { label: 'arcsin', title: 'Arcsin', insert: '\\arcsin(•)' },
    { label: 'arccos', title: 'Arccos', insert: '\\arccos(•)' },
    { label: 'arctan', title: 'Arctan', insert: '\\arctan(•)' },
    { label: 'sinh', title: 'Sinus hyperbolique', insert: '\\sinh(•)' },
    { label: 'cosh', title: 'Cosinus hyperbolique', insert: '\\cosh(•)' },
    { label: 'tanh', title: 'Tangente hyperbolique', insert: '\\tanh(•)' },
    { label: 'exp', title: 'Exponentielle', insert: 'e^{•}' },
    { label: 'ln', title: 'Logarithme naturel', insert: '\\ln(•)' },
    { label: 'log', title: 'Logarithme', insert: '\\log_{•}(•)' },
    { label: 'max', title: 'Maximum', insert: '\\max_{•}' },
    { label: 'min', title: 'Minimum', insert: '\\min_{•}' },
    { label: 'sup', title: 'Supremum', insert: '\\sup_{•}' },
    { label: 'inf', title: 'Infimum', insert: '\\inf_{•}' },
    { label: 'Re', title: 'Partie réelle', insert: '\\text{Re}(•)' },
    { label: 'Im', title: 'Partie imaginaire', insert: '\\text{Im}(•)' },
    { label: 'Ker', title: 'Noyau', insert: '\\ker(•)' },
    { label: 'rank', title: 'Rang', insert: '\\text{rank}(•)' },
  ],
  'Templates': [
    { label: 'Schrödinger', title: 'Équation de Schrödinger', insert: 'i\\hbar\\frac{\\partial}{\\partial t}|\\psi\\rangle = \\hat{H}|\\psi\\rangle' },
    { label: 'Maxwell-Gauss', title: 'Maxwell - Gauss', insert: '\\nabla \\cdot \\vec{E} = \\frac{\\rho}{\\epsilon_0}' },
    { label: 'Maxwell-Faraday', title: 'Maxwell - Faraday', insert: '\\nabla \\times \\vec{E} = -\\frac{\\partial \\vec{B}}{\\partial t}' },
    { label: 'Maxwell-Ampère', title: 'Maxwell - Ampère', insert: '\\nabla \\times \\vec{B} = \\mu_0\\vec{J} + \\mu_0\\epsilon_0\\frac{\\partial \\vec{E}}{\\partial t}' },
    { label: 'E=mc²', title: 'Énergie-masse', insert: 'E = mc^2' },
    { label: 'Euler', title: "Identité d'Euler", insert: 'e^{i\\pi} + 1 = 0' },
    { label: 'Fourier', title: 'Transformée de Fourier', insert: '\\hat{f}(\\xi) = \\int_{-\\infty}^{+\\infty} f(x)e^{-2\\pi i x\\xi}dx' },
    { label: 'Taylor', title: 'Série de Taylor', insert: 'f(x) = \\sum_{n=0}^{\\infty}\\frac{f^{(n)}(a)}{n!}(x-a)^n' },
    { label: 'Gauss', title: 'Intégrale gaussienne', insert: '\\int_{-\\infty}^{+\\infty}e^{-x^2}dx = \\sqrt{\\pi}' },
    { label: 'Stokes', title: 'Théorème de Stokes', insert: '\\oint_{\\partial\\Sigma}\\vec{F}\\cdot d\\vec{r} = \\iint_{\\Sigma}(\\nabla\\times\\vec{F})\\cdot d\\vec{S}' },
    { label: 'Newton', title: 'Loi de Newton', insert: '\\vec{F} = m\\vec{a} = m\\ddot{\\vec{r}}' },
    { label: 'Heisenberg', title: 'Inégalité Heisenberg', insert: '\\Delta x \\cdot \\Delta p \\geq \\frac{\\hbar}{2}' },
    { label: 'Cauchy-Schwarz', title: 'Cauchy-Schwarz', insert: '|\\langle u,v\\rangle|^2 \\leq \\langle u,u\\rangle\\cdot\\langle v,v\\rangle' },
    { label: 'Navier-Stokes', title: 'Navier-Stokes', insert: '\\rho\\left(\\frac{\\partial\\vec{v}}{\\partial t}+(\\vec{v}\\cdot\\nabla)\\vec{v}\\right) = -\\nabla p + \\mu\\nabla^2\\vec{v}' },
    { label: 'Riemann ζ', title: 'Zeta de Riemann', insert: '\\zeta(s) = \\sum_{n=1}^{\\infty}\\frac{1}{n^s}' },
    { label: 'Pythagore', title: 'Théorème de Pythagore', insert: 'a^2 + b^2 = c^2' },
    { label: 'Binôme', title: 'Binôme de Newton', insert: '(a+b)^n = \\sum_{k=0}^{n}\\binom{n}{k}a^{n-k}b^k' },
    { label: 'Basel', title: 'Problème de Bâle', insert: '\\sum_{n=1}^{\\infty}\\frac{1}{n^2} = \\frac{\\pi^2}{6}' },
  ],
};

function renderLatex(latex, display = false) {
  if (!latex || !latex.trim()) return '';
  try {
    return katex.renderToString(latex.replace(/•/g, '\\square'), {
      throwOnError: false,
      displayMode: display,
      output: 'html',
      // trust: true autorisait les URL javascript: dans les equations
      trust: false,
    });
  } catch {
    return '<span style="color:#E53E3E;font-size:12px">Équation invalide</span>';
  }
}

export default function MathBlock({ block, editMode, onUpdate, onDelete }) {
  const parse = () => {
    try { return JSON.parse(block.content); }
    catch { return { latex: block.content || '', display: true, caption: '' }; }
  };

  const [data, setData] = useState(parse);
  const [editing, setEditing] = useState(!block.content);
  const [form, setForm] = useState(data);
  const [activeGroup, setActiveGroup] = useState('Base');
  const textareaRef = useRef(null);
  const isDark = block.bg_color === '#1A2332';

  const save = async () => {
    await onUpdate(block.id, JSON.stringify(form));
    setData(form);
    setEditing(false);
  };

  const insertAtCursor = (text) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const current = form.latex;
    const newVal = current.substring(0, start) + text + current.substring(end);
    setForm({ ...form, latex: newVal });
    setTimeout(() => {
      ta.focus();
      const pos = newVal.indexOf('•', start);
      if (pos !== -1) {
        ta.setSelectionRange(pos, pos + 1);
      } else {
        const p = start + text.length;
        ta.setSelectionRange(p, p);
      }
    }, 0);
  };

  const preview = renderLatex(form.latex, form.display);
  const rendered = renderLatex(data.latex, data.display);

  if (editing && editMode) {
    return (
      <div>
        {/* Mode toggle */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted)' }}>Mode :</span>
          <button onClick={() => setForm({ ...form, display: false })} style={{ background: !form.display ? 'var(--blue)' : 'var(--bg)', color: !form.display ? '#fff' : 'var(--muted)', border: `1px solid ${!form.display ? 'var(--blue)' : 'var(--border)'}`, padding: '4px 12px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}>Inline</button>
          <button onClick={() => setForm({ ...form, display: true })} style={{ background: form.display ? 'var(--blue)' : 'var(--bg)', color: form.display ? '#fff' : 'var(--muted)', border: `1px solid ${form.display ? 'var(--blue)' : 'var(--border)'}`, padding: '4px 12px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}>Bloc centré</button>
          <span style={{ fontSize: '11px', color: 'var(--muted)', marginLeft: '4px' }}>💡 • = emplacement du curseur</span>
        </div>

        {/* Group tabs */}
        <div style={{ display: 'flex', gap: '3px', marginBottom: '6px', flexWrap: 'wrap' }}>
          {Object.keys(TOOLBAR).map(group => (
            <button key={group} onClick={() => setActiveGroup(group)} style={{ background: activeGroup === group ? 'var(--blue)' : 'var(--bg)', color: activeGroup === group ? '#fff' : 'var(--muted)', border: `1px solid ${activeGroup === group ? 'var(--blue)' : 'var(--border)'}`, padding: '4px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', transition: 'all .15s' }}>
              {group}
            </button>
          ))}
        </div>

        {/* Toolbar buttons */}
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', padding: '8px 10px', background: '#F7F9FC', borderRadius: '10px', border: '1px solid var(--border)', marginBottom: '10px', maxHeight: '130px', overflowY: 'auto' }}>
          {TOOLBAR[activeGroup]?.map((btn, i) => (
            <button key={i} onClick={() => insertAtCursor(btn.insert)} title={btn.title}
              style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '4px 9px', fontSize: activeGroup === 'Templates' ? '10px' : '12px', cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap', transition: 'all .1s', color: '#2D3748' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#EBF4FF'; e.currentTarget.style.borderColor = '#4299E1'; e.currentTarget.style.color = '#2B6CB0'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.color = '#2D3748'; }}
            >
              {btn.label}
            </button>
          ))}
        </div>

        {/* LaTeX input */}
        <div style={{ marginBottom: '10px' }}>
          <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '4px', letterSpacing: '0.5px' }}>CODE LaTeX</label>
          <textarea ref={textareaRef} value={form.latex} onChange={e => setForm({ ...form, latex: e.target.value })}
            placeholder="Clique sur les boutons ou écris directement en LaTeX..."
            rows={3}
            style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--blue-border)', borderRadius: '10px', fontSize: '13px', fontFamily: '"Fira Code", "Courier New", monospace', outline: 'none', resize: 'vertical', lineHeight: 1.6, boxSizing: 'border-box', background: '#1A2332', color: '#E2E8F0' }}
            onFocus={e => e.target.style.borderColor = 'var(--blue)'}
            onBlur={e => e.target.style.borderColor = 'var(--blue-border)'}
          />
        </div>

        {/* Live preview */}
        <div style={{ marginBottom: '10px' }}>
          <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '4px', letterSpacing: '0.5px' }}>APERÇU EN TEMPS RÉEL</label>
          <div style={{ background: '#fff', borderRadius: '10px', padding: '16px 20px', border: '1px solid var(--border)', textAlign: form.display ? 'center' : 'left', minHeight: '52px', display: 'flex', alignItems: 'center', justifyContent: form.display ? 'center' : 'flex-start', overflowX: 'auto' }}>
            {preview ? <div dangerouslySetInnerHTML={{ __html: preview }} /> : <span style={{ color: 'var(--muted)', fontSize: '13px', fontStyle: 'italic' }}>L'aperçu apparaît ici...</span>}
          </div>
        </div>

        {/* Caption */}
        <input value={form.caption || ''} onChange={e => setForm({ ...form, caption: e.target.value })} placeholder="Légende optionnelle..."
          style={{ width: '100%', padding: '9px 14px', border: '1.5px solid var(--blue-border)', borderRadius: '8px', fontSize: '13px', outline: 'none', marginBottom: '12px', boxSizing: 'border-box' }}
          onFocus={e => e.target.style.borderColor = 'var(--blue)'} onBlur={e => e.target.style.borderColor = 'var(--blue-border)'}
        />

        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={save} style={{ background: 'var(--blue)', color: '#fff', border: 'none', padding: '9px 24px', borderRadius: '20px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(43,108,176,0.25)' }}>Sauvegarder</button>
          {block.content && <button onClick={() => { setForm(data); setEditing(false); }} style={{ background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)', padding: '9px 18px', borderRadius: '20px', fontSize: '13px', cursor: 'pointer' }}>Annuler</button>}
        </div>
      </div>
    );
  }

  return (
    <div>
      {rendered ? (
        <div>
          <div style={{ textAlign: data.display ? 'center' : 'left', padding: data.display ? '6px 0' : '0', overflowX: 'auto' }}>
            <div dangerouslySetInnerHTML={{ __html: rendered }} style={{ color: isDark ? '#E2E8F0' : 'var(--dark)' }} />
          </div>
          {data.caption && <p style={{ fontSize: '12px', color: isDark ? '#718096' : 'var(--muted)', marginTop: '8px', fontStyle: 'italic', textAlign: 'center' }}>{data.caption}</p>}
        </div>
      ) : (
        <p style={{ color: 'var(--muted)', fontStyle: 'italic', fontSize: '13px' }}>Équation vide — clique sur Modifier</p>
      )}
      {editMode && (
        <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
          <button onClick={() => setEditing(true)} style={{ background: 'var(--blue-bg)', color: 'var(--blue)', border: 'none', padding: '4px 12px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>Modifier</button>
          <button onClick={() => onDelete(block.id)} style={{ background: '#FFF5F5', color: 'var(--red)', border: 'none', padding: '4px 10px', borderRadius: '10px', fontSize: '11px', cursor: 'pointer' }}>Supprimer</button>
        </div>
      )}
    </div>
  );
}
